import type { CSSProperties } from "react";

export type PautaMarkTone = "on-dark" | "on-light" | "mono";

interface PautaMarkProps {
  size?: number | string;
  tone?: PautaMarkTone;
  title?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Pauta's canonical mark: three agenda rows, each shared by a human and an
 * agent. The widening rhythm reads as work gathering momentum without relying
 * on a letterform, robot, sparkle, or checkmark.
 *
 * Keep this geometry in sync with the source SVGs under public/brand/. It is
 * deliberately simple enough to remain recognizable at a 16px favicon.
 */
export function PautaMark({
  size = 24,
  tone = "on-dark",
  title,
  className,
  style,
}: PautaMarkProps) {
  const base = tone === "on-dark" ? "#f7f8f8" : tone === "on-light" ? "#08090a" : "currentColor";
  const purple = tone === "mono" ? "currentColor" : "#a55cff";
  const blue = tone === "mono" ? "currentColor" : "#6678e8";

  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      style={style}
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      {title ? <title>{title}</title> : null}

      <path d="M22 13h8v8h-8a4 4 0 1 1 0-8Z" fill={base} />
      <path d="M30 13h12a4 4 0 1 1 0 8H30v-8Z" fill={purple} />

      <path d="M18 28h12v8H18a4 4 0 1 1 0-8Z" fill={base} />
      <path d="M30 28h16a4 4 0 1 1 0 8H30v-8Z" fill={blue} />

      <path d="M14 43h14v8H14a4 4 0 1 1 0-8Z" fill={base} />
      <path d="M28 43h22a4 4 0 1 1 0 8H28v-8Z" fill={purple} />
    </svg>
  );
}

interface PautaLockupProps extends PautaMarkProps {
  compact?: boolean;
}

export function PautaLockup({
  size = 28,
  tone = "on-dark",
  compact = false,
  className,
  style,
}: PautaLockupProps) {
  return (
    <span
      className={className}
      style={{ display: "inline-flex", alignItems: "center", gap: compact ? 8 : 10, ...style }}
    >
      <PautaMark size={size} tone={tone} />
      <span style={{ fontSize: compact ? 13 : 15.5, fontWeight: 600, letterSpacing: "-0.02em" }}>
        Pauta
      </span>
    </span>
  );
}
